import { useState, useEffect } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useFetch, Field } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { Empty, PageHeader } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { GRADES } from "@/lib/grades";
import ModulesPanel from "@/components/ModulesPanel";
import CourseFees from "@/components/CourseFees";
import { BookOpen, GraduationCap, Plus, Trash2, Eye, DollarSign, TrendingUp, PlayCircle, LinkIcon, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MediaThumb } from "@/components/MediaThumb";
import { downloadForOffline, cacheForOffline, downloadable, offlineSet } from "@/lib/offline";

const LIB_SUBJECTS = ["Histoire", "Mathématiques", "Sciences", "Géographie", "Culture africaine", "Musique classique", "IA", "Français", "Anglais"];

function StudentProgress() {
  const { L } = useLang();
  const [grade, setGrade] = useState("all");
  const { data: rows, loading } = useFetch(() => api.get(grade === "all" ? "/lms/student-progress" : `/lms/student-progress?grade_level=${encodeURIComponent(grade)}`), [grade]);
  const fmt = (iso) => { if (!iso) return null; try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return iso.slice(0, 10); } };
  const byGrade = {};
  (rows || []).forEach((r) => { (byGrade[r.grade_level || "—"] = byGrade[r.grade_level || "—"] || []).push(r); });
  return (
    <div className="space-y-6" data-testid="student-progress">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">{L("How far each student has progressed through the HTML lessons of their grade.", "Progression de chaque élève dans les leçons HTML de son niveau.")}</p>
        <div className="w-56">
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger data-testid="sp-grade-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{L("All grades", "Tous les niveaux")}</SelectItem>
              {GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {loading ? <Empty title={L("Loading…", "Chargement…")} /> : (rows || []).length === 0 ? <Empty title={L("No students / lessons yet", "Aucun élève / leçon")} /> : Object.keys(byGrade).map((g) => (
        <div key={g} data-testid={`sp-grade-${g}`}>
          <p className="font-heading font-bold text-lg mb-2">{g}</p>
          <div className="space-y-2">
            {byGrade[g].map((r) => (
              <div key={r.student_id} className="bg-card border border-border rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`sp-student-${r.student_id}`}>
                <span className="w-48 shrink-0 font-medium truncate">{r.name}</span>
                <div className="flex-1 min-w-[120px] h-3 bg-secondary rounded overflow-hidden"><div className="h-full bg-primary" style={{ width: `${r.percent}%` }} /></div>
                <span className="w-24 text-right text-sm text-muted-foreground">{r.completed}/{r.total} · {r.percent}%</span>
                <span className="w-full sm:w-auto sm:min-w-[220px] text-xs text-muted-foreground" data-testid={`sp-last-${r.student_id}`}>
                  {r.last_completed_at
                    ? L(`Last: ${fmt(r.last_completed_at)}${r.marked_by ? ` — by ${r.marked_by}${r.marked_role === "parent" ? " (parent)" : ""}` : ""}`,
                        `Dernier : ${fmt(r.last_completed_at)}${r.marked_by ? ` — par ${r.marked_by}${r.marked_role === "parent" ? " (parent)" : ""}` : ""}`)
                    : L("Not started", "Non commencé")}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Library() {
  const { L } = useLang();
  const { data: docs, loading, reload } = useFetch(() => api.get("/lms/documents"));
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [offline, setOffline] = useState(() => offlineSet());
  const [dlBusy, setDlBusy] = useState("");
  useEffect(() => { const h = () => setOffline(offlineSet()); window.addEventListener("lms-offline-changed", h); return () => window.removeEventListener("lms-offline-changed", h); }, []);
  const saveOffline = async (d) => { setDlBusy(d.id); try { await downloadForOffline(d); toast.success(L("Saved for offline reading ✓", "Enregistré pour lecture hors-ligne ✓")); } catch { toast.error(L("Download failed", "Échec du téléchargement")); } finally { setDlBusy(""); } };
  const upload = async () => {
    if (!grade || !title.trim() || !file) { toast.error(L("Grade, title and file are required", "Niveau, titre et fichier requis")); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("grade_level", grade); fd.append("subject", subject); fd.append("title", title.trim()); fd.append("file", file);
      await api.raw.post("/lms/documents", fd);
      toast.success(L("Uploaded", "Téléversé")); setTitle(""); setFile(null); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setUploading(false); }
  };
  const addLink = async () => {
    if (!grade || !title.trim() || !url.trim()) { toast.error(L("Grade, title and link are required", "Niveau, titre et lien requis")); return; }
    try {
      await api.post("/lms/media-link", { grade_level: grade, subject, title: title.trim(), url: url.trim() });
      toast.success(L("Link added", "Lien ajouté")); setTitle(""); setUrl(""); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (d) => { if (!window.confirm(L(`Are you sure you want to delete ${d.title}?`, `Êtes-vous sûr de vouloir supprimer ${d.title} ?`))) return; try { await api.del(`/lms/documents/${d.id}`); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;
  const byGrade = {}; (docs || []).forEach((d) => { (byGrade[d.grade_level] = byGrade[d.grade_level] || []).push(d); });
  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-5" data-testid="lms-upload-card">
        <p className="font-heading font-bold mb-3 flex items-center gap-2"><Plus className="h-4 w-4" />{L("Add a resource", "Ajouter une ressource")}</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label={L("Grade / Class", "Niveau / Classe")}>
            <Select value={grade} onValueChange={setGrade}>
              <SelectTrigger data-testid="lms-grade-select"><SelectValue placeholder={L("Select grade", "Choisir le niveau")} /></SelectTrigger>
              <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={L("Subject (type any)", "Matière (libre)")}>
            <Input list="lib-subjects" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={L("e.g. History, IA, Music…", "ex. Histoire, IA, Musique…")} data-testid="lms-subject-input" />
            <datalist id="lib-subjects">{LIB_SUBJECTS.map((s) => <option key={s} value={s} />)}</datalist>
          </Field>
          <Field label={L("Title", "Titre")}><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="lms-title-input" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div className="rounded-md border border-border p-3">
            <p className="text-xs font-semibold mb-2">{L("Option A — Upload a file (PDF, HTML, .zip)", "Option A — Téléverser un fichier (PDF, HTML, .zip)")}</p>
            <Input type="file" accept=".pdf,.html,.htm,.zip" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="lms-file-input" />
            <Button className="mt-2" size="sm" onClick={upload} disabled={uploading} data-testid="lms-upload-btn">{uploading ? L("Uploading…", "Téléversement…") : L("Upload", "Téléverser")}</Button>
          </div>
          <div className="rounded-md border border-border p-3">
            <p className="text-xs font-semibold mb-2 flex items-center gap-1"><LinkIcon className="h-3.5 w-3.5" />{L("Option B — Direct link (YouTube or MP4)", "Option B — Lien direct (YouTube ou MP4)")}</p>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtube.com/watch?v=… / https://…/video.mp4" data-testid="lms-link-url" />
            <Button className="mt-2" size="sm" variant="outline" onClick={addLink} data-testid="lms-link-add-btn"><PlayCircle className="h-4 w-4 mr-1" />{L("Add link", "Ajouter le lien")}</Button>
          </div>
        </div>
      </div>
      {Object.keys(byGrade).length === 0 ? <Empty title={L("No resources yet", "Aucune ressource")} /> : Object.keys(byGrade).map((g) => (
        <div key={g} data-testid={`lms-grade-group-${g}`}>
          <p className="font-heading font-bold text-lg mb-2 flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" />{g}</p>
          <div className="space-y-2">
            {byGrade[g].map((d) => (
              <div key={d.id} className="bg-card border border-border rounded-lg" data-testid={`lms-doc-${d.id}`}>
                <div className="p-4 flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium flex items-center gap-2">
                    {d.kind === "link" && <MediaThumb url={d.url} className="h-9 w-16" testId={`lms-thumb-${d.id}`} />}
                    {d.title}
                    {d.subject && <Badge variant="secondary" className="text-xs">{d.subject}</Badge>}
                    {d.kind === "link" && <span className="text-xs bg-accent/15 text-accent rounded px-2 py-0.5">{L("Video", "Vidéo")}</span>}
                    {offline.has(d.id) && <span className="text-xs bg-sky-100 text-sky-700 rounded px-2 py-0.5 flex items-center gap-1" data-testid={`library-offline-${d.id}`}><Download className="h-3 w-3" />{L("Available offline", "Disponible hors-ligne")}</span>}
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {d.kind === "link"
                      ? <Button size="sm" variant="outline" onClick={() => window.open(d.url, "_blank", "noopener")} data-testid={`lms-play-${d.id}`}><PlayCircle className="h-4 w-4 mr-1" />{L("Play video", "Lire la vidéo")}</Button>
                      : <Button size="sm" variant="outline" onClick={() => { const opening = viewing !== d.id; setViewing(opening ? d.id : null); if (opening && downloadable(d)) cacheForOffline(d); }} data-testid={`lms-view-${d.id}`}><Eye className="h-4 w-4 mr-1" />{viewing === d.id ? L("Hide", "Masquer") : L("Preview", "Aperçu")}</Button>}
                    {downloadable(d) && <Button size="sm" variant="ghost" disabled={dlBusy === d.id} onClick={() => saveOffline(d)} data-testid={`library-download-${d.id}`}><Download className="h-4 w-4 mr-1" />{dlBusy === d.id ? L("Saving…", "…") : L("Download offline", "Télécharger hors-ligne")}</Button>}
                    <Button size="icon" variant="ghost" onClick={() => del(d)} data-testid={`lms-del-${d.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
                {viewing === d.id && d.kind !== "link" && (
                  <div className="border-t border-border p-3">
                    <div className="relative w-full" style={{ height: "70vh" }}>
                      <iframe title={d.title} src={`${API}/lms/documents/${d.id}/view#toolbar=0&navpanes=0`} className="w-full h-full border border-border rounded" data-testid={`lms-frame-${d.id}`} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AccessLog() {
  const { L } = useLang();
  const { data: rows, loading } = useFetch(() => api.get("/lms/access-log?limit=300"));
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  return (
    <div data-testid="lms-access-log">
      <p className="text-sm text-muted-foreground mb-3">{L("Who opened which document, most recent first (one entry per user & document within 10 minutes).", "Qui a ouvert quel document, du plus récent au plus ancien (une entrée par utilisateur et document sur 10 minutes).")}</p>
      {loading ? <Empty title={L("Loading…", "Chargement…")} /> : (rows || []).length === 0 ? <Empty title={L("No access recorded yet", "Aucun accès enregistré")} /> : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60"><tr>
              <th className="text-left p-2 font-semibold">{L("When", "Quand")}</th>
              <th className="text-left p-2 font-semibold">{L("User", "Utilisateur")}</th>
              <th className="text-left p-2 font-semibold">{L("Role", "Rôle")}</th>
              <th className="text-left p-2 font-semibold">{L("Document", "Document")}</th>
              <th className="text-left p-2 font-semibold">{L("Grade", "Niveau")}</th>
            </tr></thead>
            <tbody>
              {(rows || []).map((r, i) => (
                <tr key={i} className="border-t border-border" data-testid={`access-row-${i}`}>
                  <td className="p-2 whitespace-nowrap">{fmt(r.at)}</td>
                  <td className="p-2 font-medium">{r.user_name}</td>
                  <td className="p-2"><Badge variant="secondary" className="text-xs">{r.role}</Badge></td>
                  <td className="p-2">{r.doc_title}</td>
                  <td className="p-2">{r.grade_level}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function LmsAdmin({ financeOnly = false }) {
  const { L } = useLang();
  const [tab, setTab] = useState(financeOnly ? "fees" : "library");
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("LMS Dashboard", "Tableau LMS")}
        description={financeOnly
          ? L("Course fees and book lists per grade.", "Frais de cours et listes de livres par niveau.")
          : L("Manage grade course materials and the curriculum planner.", "Gérez les supports de cours par niveau et le planificateur de curriculum.")} />
      <div className="flex gap-2 mb-6">
        {!financeOnly && <Button variant={tab === "library" ? "default" : "outline"} onClick={() => setTab("library")} data-testid="lms-tab-library"><BookOpen className="h-4 w-4 mr-1" />{L("Library", "Bibliothèque")}</Button>}
        {!financeOnly && <Button variant={tab === "curriculum" ? "default" : "outline"} onClick={() => setTab("curriculum")} data-testid="lms-tab-curriculum"><GraduationCap className="h-4 w-4 mr-1" />{L("Curriculum & Progress", "Curriculum & Progression")}</Button>}
        <Button variant={tab === "fees" ? "default" : "outline"} onClick={() => setTab("fees")} data-testid="lms-tab-fees"><DollarSign className="h-4 w-4 mr-1" />{L("Course Fees / Book List", "Frais de cours / Livres")}</Button>
        {!financeOnly && <Button variant={tab === "students" ? "default" : "outline"} onClick={() => setTab("students")} data-testid="lms-tab-students"><TrendingUp className="h-4 w-4 mr-1" />{L("Student Progress", "Progression des élèves")}</Button>}
        {!financeOnly && <Button variant={tab === "access" ? "default" : "outline"} onClick={() => setTab("access")} data-testid="lms-tab-access"><Eye className="h-4 w-4 mr-1" />{L("Access Log", "Journal d'accès")}</Button>}
      </div>
      {financeOnly ? <CourseFees manage={true} /> : (tab === "library" ? <Library /> : tab === "fees" ? <CourseFees manage={true} /> : tab === "students" ? <StudentProgress /> : tab === "access" ? <AccessLog /> : <ModulesPanel canManage={true} />)}
    </div>
  );
}
